import { useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import type { SlideStatRow } from "../types";
import { SECTION_TITLE } from "./config";

type SortKey = "slide" | "students" | "avg_score" | "avg_time";
type SortDir = "asc" | "desc";

function fmtTime(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function SortIcon({ col, sort }: { col: SortKey; sort: { key: SortKey; dir: SortDir } }) {
  if (sort.key !== col) return <ChevronsUpDown className="size-3 text-muted-foreground/50" />;
  return sort.dir === "asc"
    ? <ChevronUp className="size-3 text-foreground" />
    : <ChevronDown className="size-3 text-foreground" />;
}

function Th({
  label, col, sort, onSort, className = "",
}: {
  label: string; col: SortKey; sort: { key: SortKey; dir: SortDir };
  onSort: (col: SortKey) => void; className?: string;
}) {
  return (
    <th
      className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap ${className}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <SortIcon col={col} sort={sort} />
      </span>
    </th>
  );
}

export function SlideAttendance({
  slides,
  isLesson,
}: {
  slides: SlideStatRow[];
  isLesson: boolean;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "slide", dir: "asc" });

  function onSort(col: SortKey) {
    setSort((prev) =>
      prev.key === col
        ? { key: col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: col, dir: col === "avg_score" ? "desc" : "asc" },
    );
  }

  const sorted = [...slides].sort((a, b) => {
    const aLesson = (a as unknown as Record<string, unknown>).lesson_id ?? a.lessonId ?? 0;
    const bLesson = (b as unknown as Record<string, unknown>).lesson_id ?? b.lessonId ?? 0;
    const aSlide = (a as unknown as Record<string, unknown>).slide_index ?? a.slideIndex ?? 0;
    const bSlide = (b as unknown as Record<string, unknown>).slide_index ?? b.slideIndex ?? 0;

    let cmp = 0;
    if (sort.key === "slide") {
      cmp = isLesson
        ? (aSlide as number) - (bSlide as number)
        : (aLesson as number) - (bLesson as number) || (aSlide as number) - (bSlide as number);
    } else if (sort.key === "students") {
      cmp = a.students - b.students;
    } else if (sort.key === "avg_score") {
      cmp = (a.avg_score ?? -1) - (b.avg_score ?? -1);
    } else if (sort.key === "avg_time") {
      cmp = (a.avg_time_spent_ms ?? -1) - (b.avg_time_spent_ms ?? -1);
    }
    return sort.dir === "asc" ? cmp : -cmp;
  });

  return (
    <div>
      <h2 className={`${SECTION_TITLE} mb-1`}>Per-Slide Attendance</h2>
      <p className="text-xs text-muted-foreground mb-3">Avg Q&A counts are averaged over completed attempts only.</p>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30">
              {isLesson ? null : (
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                  Lesson
                </th>
              )}
              <Th label="Slide" col="slide" sort={sort} onSort={onSort} />
              <Th label="Students" col="students" sort={sort} onSort={onSort} />
              <Th label="Avg Score" col="avg_score" sort={sort} onSort={onSort} />
              <Th label="Avg Active Time" col="avg_time" sort={sort} onSort={onSort} />
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Avg Q&A (incorrect/total)
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const lessonId = (row as unknown as Record<string, unknown>).lesson_id ?? row.lessonId;
              const slideIndex = ((row as unknown as Record<string, unknown>).slide_index ?? row.slideIndex) as number;
              return (
                <tr key={i} className="border-t hover:bg-muted/20 transition-colors">
                  {isLesson ? null : (
                    <td className="px-3 py-2 text-muted-foreground text-xs">
                      {row.lessonName ? `${row.lessonName} (${lessonId})` : `Lesson ${lessonId}`}
                    </td>
                  )}
                  <td className="px-3 py-2 tabular-nums text-foreground">{slideIndex}</td>
                  <td className="px-3 py-2 tabular-nums text-foreground">{row.students}</td>
                  <td className="px-3 py-2 tabular-nums text-foreground">
                    {row.avg_score != null ? row.avg_score.toFixed(1) : "—"}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-foreground">{fmtTime(row.avg_time_spent_ms)}</td>
                  <td className="px-3 py-2 tabular-nums text-foreground">
                    {row.avg_qar_total === undefined
                      ? <span className="text-muted-foreground italic text-xs">outdated</span>
                      : row.avg_qar_total != null && row.avg_qar_total > 0
                        ? (() => {
                            const pct = row.avg_qar_incorrect != null
                              ? Math.round((row.avg_qar_incorrect / row.avg_qar_total!) * 100)
                              : null;
                            return `${row.avg_qar_incorrect?.toFixed(1) ?? "—"} / ${row.avg_qar_total.toFixed(1)}${pct != null ? ` (${pct}%)` : ""}`;
                          })()
                        : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
